import { Body, Icon } from '@assembly-js/design-system';
import React from 'react';
import { useChannelStateContext } from 'stream-chat-react';

import { PortalConfigContext } from 'src/context';

import { AIAssistantLoadingIndicator } from './AssistantLoadingIndicator';

interface AIAssistantStatusIndicatorProps {
  status: string;
  metadata?: {
    tool?: string;
    query?: string;
    errorMessage?: string;
    messageId?: string;
  };
  getCompanyFromId?: (companyId: string) => { name?: string } | null;
  getClientFromId?: (clientId: string) => { name?: string } | null;
  onClearStatus?: () => void;
  onCancelStream?: () => void;
  isCancelable?: boolean;
}

// Type guard to check if a value is a record with a specific key
const hasProperty = <T extends string>(
  obj: unknown,
  key: T,
): obj is Record<T, unknown> => {
  return typeof obj === 'object' && obj !== null && key in obj;
};

// Safe accessor for channel data fields
const getChannelDataField = (
  channelData: unknown,
  field: string,
): string | undefined => {
  if (!hasProperty(channelData, field)) {
    return undefined;
  }

  const value = channelData[field];
  if (typeof value === 'string') {
    return value;
  }

  return undefined;
};

// Helper function to extract entity name from channel context using lookup functions
const useEntityNameFromChannel = (
  status: string,
  getCompanyFromId?: (companyId: string) => { name?: string } | null,
  getClientFromId?: (clientId: string) => { name?: string } | null,
) => {
  const { channel } = useChannelStateContext();
  const portalConfig = React.useContext(PortalConfigContext);

  if (!channel?.data && status !== 'get_workspace_context') return null;

  switch (status) {
    case 'get_company_context': {
      const companyId = getChannelDataField(channel.data, 'companyID');
      if (companyId && getCompanyFromId) {
        const company = getCompanyFromId(companyId);
        return company?.name || null;
      }
      return null;
    }

    case 'get_client_context': {
      const clientId = getChannelDataField(channel.data, 'clientID');
      if (clientId && getClientFromId) {
        const client = getClientFromId(clientId);
        return client?.name || null;
      }
      return null;
    }

    case 'get_workspace_context': {
      // Workspace name comes from PortalConfigContext
      // There's only ever one workspace in memory at a time
      return portalConfig?.name || null;
    }

    default:
      return null;
  }
};

const getStatusText = (
  status: string,
  metadata?: { tool?: string; query?: string; errorMessage?: string },
  entityName?: string | null,
) => {
  switch (status) {
    case 'sending':
      return '';
    case 'searching_web':
      return 'Searching the web';
    case 'generating_response':
      return 'Generating response';
    case 'using_tools':
      return metadata?.tool ? `Using ${metadata.tool}` : 'Using tools';
    case 'get_client_context':
      return entityName
        ? `Retrieving ${entityName}'s information`
        : 'Retrieving client information';
    case 'get_company_context':
      return entityName
        ? `Retrieving ${entityName}'s information`
        : 'Retrieving company information';
    case 'get_workspace_context':
      return entityName
        ? `Retrieving ${entityName}'s information`
        : 'Retrieving workspace information';
    case 'search':
      return metadata?.query
        ? `Searching workspace for "${metadata.query}"`
        : 'Searching workspace';
    case 'taking_longer':
      return 'This is taking longer than usual';
    case 'error':
      return metadata?.errorMessage || 'An error occurred';
    case 'canceling':
      return 'Canceling';
    case 'canceled':
      return 'Canceled';
    default: {
      const thinkingSynonyms = [
        'Thinking',
        'Processing',
        'Analyzing',
        'Working',
      ];
      const randomIndex = Math.floor(Math.random() * thinkingSynonyms.length);
      return thinkingSynonyms[randomIndex];
    }
  }
};

export const AssistantStatusIndicator = ({
  status,
  metadata,
  getCompanyFromId,
  getClientFromId,
  onClearStatus,
  onCancelStream,
  isCancelable,
}: AIAssistantStatusIndicatorProps) => {
  const entityName = useEntityNameFromChannel(
    status,
    getCompanyFromId,
    getClientFromId,
  );
  const { channel } = useChannelStateContext();
  const statusText = React.useMemo(
    () => getStatusText(status, metadata, entityName),
    [status, metadata, entityName],
  );
  const isError = status === 'error';
  const isStreamingStatus = status && status !== 'error' && status !== '';

  const handleClearStatus = () => {
    if (onClearStatus && channel?.cid) {
      onClearStatus();
    }
  };

  const handleCancelStream = () => {
    if (onCancelStream && channel?.cid) {
      onCancelStream();
    }
  };

  return (
    <div className="flex items-center gap-2 h-5">
      {isError ? (
        <span className="text-red-500">❌</span>
      ) : (
        <AIAssistantLoadingIndicator animate={status !== 'canceled'} />
      )}
      <Body
        size="sm"
        tag="div"
        className={[
          isError ? 'text-red-500' : '',
          'max-w-full',
          'whitespace-nowrap',
          'overflow-hidden',
          'text-ellipsis',
        ].join(' ')}
      >
        {statusText}
      </Body>
      {isError && onClearStatus && (
        <button
          onClick={handleClearStatus}
          className="ml-auto appearance-none border-none cursor-pointer"
        >
          <Icon icon="Close" className="w-4 h-4 fill-current" />
        </button>
      )}
      {!isError && isStreamingStatus && isCancelable && onCancelStream && (
        <button
          onClick={handleCancelStream}
          className="ml-auto appearance-none border-none cursor-pointer bg-white rounded-[50%] w-5 h-5 flex items-center justify-center p-0"
          title="Cancel AI response"
          aria-label="Cancel AI response"
        >
          <Icon icon="SquareSolid" className="w-2 h-2 fill-current" />
        </button>
      )}
    </div>
  );
};
